import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSpring, animated } from '@react-spring/web';

import { Search, Maximize2, Minimize2 } from 'lucide-react';
import { GitStatusButton } from '@/components/molecules/metadata_v1/GitStatusButton';
import { EditorLayout } from '@/components/organisms/metadata_v1';
import { useMetadataTypes, useCreateRetrieval } from '@/hooks/metadata_v1';
import { notifyFileSaved } from '@/services/chat/chatService';
import { useChatHistoryStore } from '@/stores/chatHistoryStore';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { useRetrievalProgressInitialStore } from '@/stores/retrievalProgressInitialStore';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';

export function MetadataV1() {
  const activeSession = useChatHistoryStore((s) => s.activeSession);
  const selectedOrg = useAuthStoreV1((s) => s.selectedOrg);

  const queryClient = useQueryClient();
  const { data: metadataTypes, isLoading: isLoadingTypes, error: typesError } = useMetadataTypes();
  const { createRetrieval, isPending: isCreateRetrievalPending } = useCreateRetrieval();

  const latestRetrieval = selectedOrg?.latestMetadataRetrieval;
  const orgId = selectedOrg?.id;

  // Persist a flag when the INITIAL_RETRIEVAL completes so that a subsequent
  // sync cancellation (which reuses the same retrieval record and flips status
  // back to CANCELLED) never re-shows the "Fetch your metadata" empty state.
  const initialDoneKey = orgId ? `ncoder.initial_done.${orgId}` : null;
  useEffect(() => {
    if (
      initialDoneKey &&
      latestRetrieval?.retrievalType === 'INITIAL_RETRIEVAL' &&
      latestRetrieval.status === 'COMPLETED'
    ) {
      localStorage.setItem(initialDoneKey, '1');
    }
  }, [initialDoneKey, latestRetrieval?.retrievalType, latestRetrieval?.status]);

  const hasEverCompletedInitialFetch =
    initialDoneKey ? localStorage.getItem(initialDoneKey) === '1' : false;

  const canStartInitialFetch =
    selectedOrg != null &&
    (latestRetrieval == null ||
      (!hasEverCompletedInitialFetch &&
        latestRetrieval.retrievalType === 'INITIAL_RETRIEVAL' &&
        (latestRetrieval.status === 'FAILED' || latestRetrieval.status === 'CANCELLED')));

  const isInitialFetchInProgress = useRetrievalProgressInitialStore((s) => s.isConnected);
  const isFetchingMetadata = isCreateRetrievalPending || isInitialFetchInProgress;

  const onStartInitialFetch = useCallback(async () => {
    if (!selectedOrg?.id) return;
    try {
      const result = await createRetrieval({ organizationId: selectedOrg.id });
      // Refresh user-data immediately so selectedOrg.latestMetadataRetrieval is updated
      // with the new retrieval ID — without this, useMetadataTypes stays disabled
      // (retrievalId: undefined) until the retrieval completes.
      await queryClient.invalidateQueries({ queryKey: ['user-data'] });
      useRetrievalProgressInitialStore.getState().connect(result.id);
    } catch (err) {
      console.error('Failed to start initial retrieval:', err);
      toast.error('Failed to start metadata fetch');
    }
  }, [selectedOrg?.id, createRetrieval, queryClient]);

  // Post-save analysis: notify the agentic backend so it runs test coverage + impact analysis.
  // Uses the active chat session (if any) so results appear in the chat.
  const handleFileSaved = useCallback(async (data: { id: string; fileName: string; metadataType: string }) => {
    const sessionUuid = activeSession?.threadId;
    const orgId = selectedOrg?.id;
    if (!sessionUuid || !orgId) {
      // No active chat session — skip silently (user isn't in a chat context)
      return;
    }
    try {
      await notifyFileSaved(sessionUuid, {
        file_name: data.fileName,
        org_id: orgId,
        metadata_type: data.metadataType,
      });
      toast.success('Post-save analysis triggered — check the chat for results.', { duration: 3000 });
    } catch {
      // Non-critical — don't block the save flow
    }
  }, [activeSession?.threadId, selectedOrg?.id]);

  const [isQuickOpenOpen, setIsQuickOpenOpen] = useState(false);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [isPortalMounted, setIsPortalMounted] = useState(false);
  const isFocusModeRef = useRef(isFocusMode);
  isFocusModeRef.current = isFocusMode;
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac');

  // Mount portal first, then spring animates in. On exit, spring animates out then unmounts.
  const focusSpring = useSpring({
    opacity: isFocusMode ? 1 : 0,
    scale: isFocusMode ? 1 : 0.94,
    config: { tension: 220, friction: 22 },
    onRest: () => {
      if (!isFocusModeRef.current) setIsPortalMounted(false);
    },
  });

  const enterFocusMode = () => { setIsPortalMounted(true); setIsFocusMode(true); };
  const exitFocusMode  = () => { setIsFocusMode(false); };

  // ESC exits focus mode
  useEffect(() => {
    if (!isFocusMode) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') exitFocusMode(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isFocusMode]);

  const editorContent = (isFocusMode: boolean) => (
    <>
      {/* Slim top bar — centered search + focus mode toggle */}
      <div className="flex items-center gap-2 pb-1.5 px-4 shrink-0 min-w-0">
        <div className="flex-1 min-w-0" />
        <button
          type="button"
          onClick={() => setIsQuickOpenOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 w-full max-w-[600px] min-w-0 rounded-md bg-muted/50 border border-border/60 text-xs text-muted-foreground hover:bg-muted hover:border-border transition-colors"
        >
          <Search className="h-3 w-3 shrink-0" />
          <span className="flex-1 text-left truncate">Search files...</span>
          <kbd className="font-sans text-[10px] bg-background border border-border/60 rounded px-1 py-0.5 leading-none shrink-0">
            {isMac ? '⌘P' : 'Ctrl+P'}
          </kbd>
        </button>

        <div className="flex-1 min-w-0 flex items-center justify-end gap-1">
          <GitStatusButton />
          <button
            type="button"
            onClick={isFocusMode ? exitFocusMode : enterFocusMode}
            title={isFocusMode ? 'Exit focus mode (Esc)' : 'Focus mode'}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
          >
            {isFocusMode ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <EditorLayout
          metadataTypes={metadataTypes ?? undefined}
          isLoadingFolders={isLoadingTypes}
          onFileSaved={handleFileSaved}
          error={typesError ?? undefined}
          hasNoRetrieval={canStartInitialFetch}
          onStartInitialFetch={onStartInitialFetch}
          isFetchingMetadata={isFetchingMetadata}
          isQuickOpenOpen={isQuickOpenOpen}
          onQuickOpenChange={setIsQuickOpenOpen}
        />
      </div>
    </>
  );

  return (
    <>
      {/* Normal view — hidden while portal is mounted */}
      {!isPortalMounted && (
        <div className="flex h-full flex-col animate-fade-in">
          {editorContent(false)}
        </div>
      )}

      {/* Animated focus-mode portal */}
      {isPortalMounted && createPortal(
        <animated.div
          style={{
            ...focusSpring,
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
          }}
          className="bg-background flex flex-col p-2"
        >
          {editorContent(true)}
        </animated.div>,
        document.body
      )}
    </>
  );
}

export default MetadataV1;
